import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@bem-react/classname';
import { AppSwitch, AppText } from '@frontend-modules/ui-kit';
import { TaskDeleteModal, TaskForm, TaskView } from '@fsd-features/task';
import { TTaskFormValue } from '@fsd-shared/models/task';
import moment from 'moment';

import { ITaskSettingsProps } from './TaskSettings.types';

import './TaskSettings.style.scss';

const CnTaskSettings = cn('task-settings');

export const TaskSettings: React.FC<ITaskSettingsProps> = (props) => {
    const {
        task,
        settingCheckboxes,
        handleDelete,
        handleTitleClick,
        handleTaskSelect,
        handleChangeTaskSettings,
        handleSetTaskCheckboxes,
        disabledSelect,
        canRepeatTask = false,
        isTestLesson = false,
        showCheckboxes = true,
        interval,
        periodsData,
        nextOnlineLessonsList,
    } = props;

    const { id = 0, title, isTemplate = false, isForMethodist = false, isPosted = false } = task;
    const [isSelected, setIsSelected] = useState(canRepeatTask ? true : isPosted);
    const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
    const [shouldGiveToNewStudents, setShouldGiveToNewStudents] = useState(
        !canRepeatTask ? settingCheckboxes?.selectNew?.checked ?? false : false,
    );
    const [shouldGiveToAllStudents, setShouldGiveToAllStudents] = useState(
        !canRepeatTask ? settingCheckboxes?.selectAll?.checked ?? false : false,
    );

    const taskBlockRef = useRef<HTMLDivElement>(null);

    const [formattedDayAndMonth, formattedDatetimeStart, formattedDatetimeEnd] = useMemo<[string, string, string]>(
        () => [
            moment(interval?.datetimeStart).format('DD.MM'),
            moment(interval?.datetimeStart).format('HH:mm'),
            moment(interval?.datetimeEnd).format('HH:mm'),
        ],
        [interval],
    );

    const handleCloseDeleteModal = () => {
        setIsShowDeleteModal(false);
    };

    const handleDeleteSubmit = useCallback(() => {
        if (handleDelete) {
            handleDelete(id);
            setIsSelected(false);
        }
    }, [handleDelete, id]);

    const handleFieldsSelected = (values: TTaskFormValue, hasErrors: boolean) => {
        const isEditableTask = isSelected && (!isPosted || canRepeatTask);
        if (isEditableTask) {
            handleChangeTaskSettings?.(values, hasErrors);
        }
    };

    useEffect(() => {
        if (isSelected) {
            taskBlockRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [isSelected]);

    useEffect(() => handleTaskSelect?.(isSelected), [isSelected]);

    useEffect(() => {
        handleSetTaskCheckboxes?.({
            isNewStudents: shouldGiveToNewStudents,
            isAllStudents: shouldGiveToAllStudents,
        });
    }, [shouldGiveToNewStudents, shouldGiveToAllStudents]);

    return (
        <>
            <div className={CnTaskSettings()} ref={taskBlockRef}>
                <TaskView
                    title={title}
                    isSelected={isSelected}
                    isTemplate={isTemplate}
                    isPosted={isPosted}
                    canRepeatTask={canRepeatTask}
                    isForMethodist={isForMethodist}
                    handleSelect={setIsSelected}
                    onTitleClick={() => handleTitleClick?.(id)}
                    withCheckbox={!disabledSelect}
                    disabledSelect={disabledSelect}
                    handleDeleteButtonClick={() => setIsShowDeleteModal(true)}
                />
                {interval && (
                    <AppText
                        text={`${formattedDayAndMonth} c ${formattedDatetimeStart} по ${formattedDatetimeEnd}`}
                        textStyle={'DesktopFootNotes'}
                    />
                )}
                {isSelected && (!isPosted || canRepeatTask) && (
                    <TaskForm
                        interval={interval}
                        isTestLesson={isTestLesson}
                        onFieldsSelected={handleFieldsSelected}
                        periodsData={periodsData}
                        nextOnlineLessonsList={nextOnlineLessonsList}
                    />
                )}
                {isSelected && showCheckboxes && (
                    <div className={CnTaskSettings('switchers-block')}>
                        {/*{(!isPosted || canRepeatTask) && (*/}
                        <div className={CnTaskSettings('switchers-block_switcher')}>
                            <AppSwitch
                                value={shouldGiveToAllStudents}
                                label={'Выдать всем'}
                                size={'small'}
                                disabled={settingCheckboxes?.selectAll?.disabled ?? canRepeatTask}
                                onChange={setShouldGiveToAllStudents}
                            />
                        </div>
                        {/*)}*/}
                        {/*<div className={CnTaskSettings('switchers-block_switcher')}>*/}
                        {/*    <AppSwitch*/}
                        {/*        value={shouldGiveToNewStudents}*/}
                        {/*        label={'Выдавать вновь прибывшим'}*/}
                        {/*        size={'small'}*/}
                        {/*        onChange={setShouldGiveToNewStudents}*/}
                        {/*        disabled={settingCheckboxes?.selectNew?.disabled}*/}
                        {/*    />*/}
                        {/*</div>*/}
                    </div>
                )}
            </div>
            {isShowDeleteModal && (
                <TaskDeleteModal
                    isOpen={isShowDeleteModal}
                    handleCloseDeleteModal={handleCloseDeleteModal}
                    handleDeleteSubmit={handleDeleteSubmit}
                />
            )}
        </>
    );
};
